import React, { useState, useEffect } from 'react';

const MetaNoContactHistory = ({ recordId, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, [recordId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const response = await fetch(`${API_URL}/meta-no-contact/${recordId}/history`);
      
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      } else {
        console.error('Error al cargar historial:', response.status);
        setHistory([]);
      }
    } catch (error) {
      console.error('Error al cargar historial:', error);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionIcon = (actionType) => {
    const icons = {
      'status_changed': '🔄',
      'comment_added': '💬',
      'record_updated': '✏️',
      'record_created': '➕',
      'follow_up': '📞',
      'email_sent': '📧',
      'meeting_scheduled': '📅',
      'issue_resolved': '✅',
      'escalated': '⚠️'
    };
    return icons[actionType] || '📝';
  };

  const getActionLabel = (actionType) => {
    const labels = {
      'status_changed': 'Cambio de Estado',
      'comment_added': 'Comentario Agregado',
      'record_updated': 'Registro Actualizado',
      'record_created': 'Registro Creado',
      'follow_up': 'Seguimiento Realizado',
      'email_sent': 'Email Enviado',
      'meeting_scheduled': 'Reunión Programada',
      'issue_resolved': 'Problema Resuelto',
      'escalated': 'Escalado'
    };
    return labels[actionType] || 'Acción Realizada';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">📋 Historial de Acciones</h2>
              <p className="text-purple-100 mt-1">
                Registro completo de actividades para este cliente sin contacto Meta
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-purple-100 hover:text-white transition-colors p-2 hover:bg-purple-700 rounded-lg"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              <span className="ml-3 text-lg text-gray-600">Cargando historial...</span>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📋</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No hay historial disponible
              </h3>
              <p className="text-gray-500">
                Aún no se han registrado acciones para este cliente.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Timeline */}
              <div className="relative">
                {history.map((item, index) => (
                  <div key={item.id} className="relative flex items-start space-x-4 pb-8">
                    {/* Timeline line */}
                    {index < history.length - 1 && (
                      <div className="absolute left-6 top-12 w-0.5 h-full bg-gray-200"></div>
                    )}
                    
                    {/* Icon */}
                    <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center text-xl">
                      {getActionIcon(item.action_type)}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="text-sm font-medium text-gray-900">
                            {getActionLabel(item.action_type)}
                          </h4>
                          <span className="text-xs text-gray-500">
                            {formatDate(item.created_at)}
                          </span>
                        </div>
                        
                        {item.description && (
                          <p className="text-sm text-gray-700 mb-2">
                            {item.description}
                          </p>
                        )}
                        
                        {item.details && (
                          <div className="text-xs text-gray-600 bg-white rounded p-2">
                            <strong>Detalles:</strong> {item.details}
                          </div>
                        )}
                        
                        {item.user_name && (
                          <div className="flex items-center mt-2 text-xs text-gray-500">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            Por: {item.user_name}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {history.length > 0 && (
                <>Total de acciones registradas: <strong>{history.length}</strong></>
              )}
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetaNoContactHistory;
